/**
 * Dependency Injection Container for Admin App
 * 
 * Simple DI container for client-side dependencies.
 */
export class DIContainer {
  private services = new Map<string, any>();
  private singletons = new Map<string, any>();

  /**
   * Register a service factory
   */
  register<T>(
    key: string,
    factory: (container: DIContainer) => T,
    singleton = false
  ): void {
    if (singleton) {
      this.singletons.set(key, factory);
    } else {
      this.services.set(key, factory);
    }
  }

  /**
   * Register a singleton instance directly
   */
  registerSingleton<T>(key: string, instance: T): void {
    this.singletons.set(key, () => instance);
  }

  /**
   * Resolve a service
   */
  resolve<T>(key: string): T {
    // Check singletons first
    if (this.singletons.has(key)) {
      const factory = this.singletons.get(key);
      if (typeof factory === 'function') {
        const instance = factory(this);
        this.singletons.set(key, () => instance); // Cache the instance
        return instance;
      }
      return factory();
    }

    // Check regular services
    if (this.services.has(key)) {
      const factory = this.services.get(key);
      return factory(this);
    }

    throw new Error(`Service not found: ${key}`);
  }

  /**
   * Check if a service is registered
   */
  has(key: string): boolean {
    return this.services.has(key) || this.singletons.has(key);
  }
}

